import * as tf from '@tensorflow/tfjs';

// Cache to store loaded models
const modelCache = {};

/**
 * Loads a TensorFlow.js model from the specified path
 * @param {string} modelPath - The path to the model directory
 * @returns {Promise<tf.GraphModel>} - The loaded model
 */
export const loadModel = async (modelPath) => {
  // Check if the model is already loaded
  if (modelCache[modelPath]) {
    console.log(`Using cached model: ${modelPath}`);
    return modelCache[modelPath];
  }

  try {
    console.log(
      `Loading model from: ${process.env.PUBLIC_URL}/models/${modelPath}/model.json`
    );

    // Load the model
    const model = await tf.loadGraphModel(
      `${process.env.PUBLIC_URL}/models/${modelPath}/model.json`
    );
    console.log('Model loaded successfully:', model);

    // Examine model inputs and outputs
    console.log(
      'Model input shapes:',
      model.inputs.map((input) => input.shape)
    );
    console.log(
      'Model output shapes:',
      model.outputs.map((output) => output.shape)
    );

    // Warm up the model with a dummy prediction
    const dummyInput = tf.zeros([1, 224, 3, 224]);
    console.log(
      'Running warm-up prediction with dummy input of shape:',
      dummyInput.shape
    );
    const warmupResult = await model.predict(dummyInput);
    console.log(
      'Warm-up prediction outputs:',
      Array.isArray(warmupResult)
        ? warmupResult.map((t) => t.shape)
        : warmupResult.shape
    );

    // Clean up warmup resources
    dummyInput.dispose();
    if (Array.isArray(warmupResult)) {
      warmupResult.forEach((t) => t.dispose());
    } else {
      warmupResult.dispose();
    }

    // Cache the model
    modelCache[modelPath] = model;
    return model;
  } catch (error) {
    console.error('Error loading model:', error);
    throw new Error(`Failed to load model: ${error.message}`);
  }
};

/**
 * Preprocesses an image for model input
 * @param {HTMLImageElement|string} image - The image to process (DOM element or data URL)
 * @returns {Promise<tf.Tensor>} - The preprocessed tensor
 */
export const preprocessImage = async (image) => {
  console.log('Preprocessing image, type:', typeof image);

  if (typeof image === 'string') {
    // If image is a data URL, create an HTMLImageElement
    return new Promise((resolve, reject) => {
      const imgEl = new Image();
      imgEl.onload = () => {
        console.log(
          'Image loaded, dimensions:',
          imgEl.width,
          'x',
          imgEl.height
        );
        // We create a new scope for the tensor operations, outside of the Promise
        const tensor = tf.tidy(() => preprocessImageElement(imgEl));
        resolve(tensor);
      };
      imgEl.onerror = (err) => {
        console.error('Error loading image:', err);
        reject(err);
      };
      imgEl.src = image;
    });
  } else {
    // If image is already an HTML element
    console.log('Processing HTML image element');
    return tf.tidy(() => preprocessImageElement(image));
  }
};

/**
 * Processes an image element into a tensor for model input
 * @param {HTMLImageElement} imgElement - The image element to process
 * @returns {tf.Tensor} - The preprocessed tensor
 */
const preprocessImageElement = (imgElement) => {
  // Read the image as a tensor
  console.log('Converting image to tensor');
  const img = tf.browser.fromPixels(imgElement);
  console.log('Initial image tensor shape:', img.shape);

  // Resize the image to 224x224 (model's expected input size)
  console.log('Resizing image to 224x224');
  const resized = tf.image.resizeBilinear(img, [224, 224]);
  console.log('Resized tensor shape:', resized.shape);

  // Normalize pixel values to [0, 1]
  console.log('Normalizing pixel values');
  const normalized = resized.div(tf.scalar(255));

  // The model expects input in the format [batch, height, channels, width]
  // which is unusual, but we need to follow it
  console.log('Reshaping tensor for model input');
  const batched = normalized.expandDims(0);
  console.log('Batched tensor shape:', batched.shape);

  // Transpose from [batch, height, width, channels] to [batch, height, channels, width]
  const transposed = batched.transpose([0, 1, 3, 2]);
  console.log('Transposed tensor shape (final):', transposed.shape);

  return transposed;
};

/**
 * Performs age and gender prediction on an image
 * @param {tf.GraphModel} model - The loaded TensorFlow.js model
 * @param {string} imageData - The image data URL to predict on
 * @returns {Promise<Object>} - The prediction results
 */
export const predictAgeGender = async (model, imageData) => {
  try {
    console.log('Starting prediction with model');

    // Preprocess the image
    const tensor = await preprocessImage(imageData);
    console.log('Preprocessed image tensor shape:', tensor.shape);

    // Run the prediction
    console.log('Running model.predict()');
    const predictions = await model.predict(tensor);
    console.log(
      'Raw prediction results:',
      Array.isArray(predictions)
        ? predictions.map((p) => ({ shape: p.shape, dataType: p.dtype }))
        : { shape: predictions.shape, dataType: predictions.dtype }
    );

    // Process the results
    let age, gender, confidence;
    let genderProbsArray = null;

    if (Array.isArray(predictions)) {
      // Extract age from first output
      const ageData = await predictions[0].data();
      console.log('Age data raw value (scaled):', ageData[0]);
      age = Math.round(ageData[0] * 100); // Scale age appropriately

      // Extract gender from second output
      const genderData = await predictions[1].data();
      genderProbsArray = Array.from(genderData); // Store gender probs before disposal
      console.log('Gender data raw values:', genderData);

      // Log prediction details for verification
      console.log('PREDICTION DETAILS:');
      console.log('  - Raw age value:', ageData[0], '→ Scaled age:', age);
      console.log(
        '  - Gender probabilities:',
        genderData
          .map((v, i) => `Class ${i}: ${(v * 100).toFixed(2)}%`)
          .join(', ')
      );

      // Assuming gender is binary classification with 2 outputs [male_prob, female_prob]
      gender = genderData[0] > genderData[1] ? 'Male' : 'Female';
      confidence = Math.max(genderData[0], genderData[1]);
    } else {
      console.error('Unexpected model output format');
      throw new Error('Model output format is not as expected');
    }

    // Clean up
    tensor.dispose();
    if (Array.isArray(predictions)) {
      predictions.forEach((p) => p.dispose());
    } else {
      predictions.dispose();
    }

    console.log('Final prediction results:', { age, gender, confidence });

    // Return the predictions
    return {
      age,
      gender,
      confidence,
      raw: {
        ageValue: age / 100, // Store the unscaled value for debugging
        genderProbs: genderProbsArray, // Use pre-stored array instead of accessing disposed tensor
      },
    };
  } catch (error) {
    console.error('Error during prediction:', error);
    throw new Error(`Prediction failed: ${error.message}`);
  }
};

/**
 * Unloads a model from memory
 * @param {string} modelPath - The path of the model to unload
 */
export const unloadModel = (modelPath) => {
  if (modelCache[modelPath]) {
    modelCache[modelPath].dispose();
    delete modelCache[modelPath];
    console.log(`Model ${modelPath} unloaded from memory`);
  }
};

/**
 * Clears all loaded models from memory
 */
export const clearAllModels = () => {
  Object.keys(modelCache).forEach((key) => {
    modelCache[key].dispose();
    delete modelCache[key];
  });
  console.log('All models cleared from memory');
};
